import { CommonPageWrapper } from 'admin/components/wrappers';
import { PageMainLabel } from 'app/components/SHLabel';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminDropDown from 'app/components/AdminDropDown';
import AdminCustomButton from 'app/components/AdminCustomButton';
import useCommonLookUpDatas from 'hooks/useCommonLookupData';
import { getLookupArray } from 'utils/common';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import CustomBreadCrumb, {
  breadCrumbDataModel,
} from 'app/components/CustomBreadCrumb';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import { GridColumnProps } from '@progress/kendo-react-grid';
import useShowDetailsApi from 'admin/hooks/useShowDetailsApi';
import { showDetailsCrumbItems } from './static';
import useShowApi from 'admin/hooks/useShowApi';
import { useErrorHandlingSlice } from 'app/error/slice';
import { useDispatch } from 'react-redux';
import { GeneralLookup } from 'services/openapi';

export const ShowDetailsPage = () => {
  const { showid } = useParams<{ showid?: string }>();
  const navigate = useNavigate();
  const {
    getShowDetails,
    showDetailsData,
    setShowDetailsData,
    downloadShowDetails,
    loading,
  } = useShowDetailsApi();
  const { loading: showDetailsLoading, handleDownloadShowDetails } =
    useShowApi();
  const dispatch = useDispatch();
  const { actions } = useErrorHandlingSlice();
  const [pageSize, setPageSize] = useState<number>(50);
  const [pageSkip, setPageSkip] = useState<number | undefined>(0);
  const [pageSizeSelected, setPageSizeSelected] = useState<GeneralLookup>({
    id: '50',
    name: '50',
  });

  // Page size options for the dropdown
  const pageSizeOptions: GeneralLookup[] = [
    { id: '25', name: '25' },
    { id: '50', name: '50' },
    { id: '100', name: '100' },
    { id: '200', name: '200' },
  ];
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingWord, setIsDownloadingWord] = useState(false);
  const { getAllLookupData, lookUps, showLookUps, getShowLookupWithSeasinId } =
    useCommonLookUpDatas();
  const [breadcrumbPreData, setBreadcrumbPreData] = useState<
    breadCrumbDataModel[]
  >(showDetailsCrumbItems);
  const [selectedShow, setSelectedShow] = useState({
    id: '',
    name: '',
  });
  const [selectedSeason, setSelectedSeason] = useState({
    id: '',
    name: '',
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize with the active season (isAdminLive: true)
  useEffect(() => {
    getAllLookupData('SEASON');
  }, []);

  // Set default season once lookups are loaded
  useEffect(() => {
    if (lookUps && lookUps.length > 0 && !isInitialized) {
      const seasonLookups = getLookupArray('SEASON', lookUps);
      const activeSeason = seasonLookups.find(
        (season: any) => season.isAdminLive === true
      );
      if (activeSeason) {
        setSelectedSeason({
          id: activeSeason.id,
          name: activeSeason.name,
        });
        getShowLookupWithSeasinId(activeSeason.id);
        setIsInitialized(true);
      }
    }
  }, [lookUps, isInitialized]);

  // When navigated from Shows table with a showid param, pre-select the show
  useEffect(() => {
    if (showid && showLookUps && showLookUps.length > 0) {
      const matchedShow = showLookUps.find((show: any) => show.id === showid);
      if (matchedShow) {
        setSelectedShow({ id: matchedShow.id, name: matchedShow.name });
        getShowDetails(matchedShow.id, 0, pageSize);
      }
    }
  }, [showid, showLookUps]);

  const handleSeasonChange = (data: any) => {
    setSelectedSeason(data);
    setSelectedShow({
      id: '',
      name: '',
    });
    setPageSkip(0);
    getShowLookupWithSeasinId(data.id);
  };

  const handleShowChange = (data: any) => {
    setSelectedShow({
      id: data.id,
      name: data.name,
    });
  };

  const transformedShowLookUps = showLookUps?.map((show: any) => ({
    ...show,
    name: show.code ? `${show.name} (${show.code})` : show.name,
  })) ?? [];

  const handlePageSizeChange = (data: GeneralLookup) => {
    setPageSizeSelected(data);
    const newPageSize = parseInt(data.id);
    setPageSize(newPageSize);
    setPageSkip(0);
    getShowDetails(selectedShow.id, 0, newPageSize);
  };

  const handleSaveClick = () => {
    setPageSkip(0);
    getShowDetails(selectedShow.id, 0, pageSize);
    navigate(`/admin/reports/show-details/${selectedShow.id}`, { replace: true });
  };

  const handleCancelClick = () => {
    setShowDetailsData(undefined);
    setSelectedShow({
      id: '',
      name: '',
    });
    navigate('/admin/reports/show-details', { replace: true });
  };

  const handlePageChange = (event: any) => {
    setPageSkip(event.page.skip);
    getShowDetails(selectedShow.id, event.page.skip, pageSize);
  };

  const handleDownloadRiderpointReport = async () => {
    if (isDownloadingExcel) return;

    try {
      setIsDownloadingExcel(true);
      const url = await downloadShowDetails(selectedShow.id);
      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);

          dispatch(
            actions.dispatchToast({
              errorTxt: 'Excel file downloaded successfully',
              show: true,
              errorType: 'success',
            }),
          );
        })
        .catch(error => {
          console.error('Download error:', error);
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Failed to download Excel file',
              show: true,
              errorType: 'error',
            }),
          );
        })
        .finally(() => {
          setIsDownloadingExcel(false);
        });

      return url;
    } catch (error) {
      setIsDownloadingExcel(false);
      dispatch(
        actions.dispatchToast({
          errorTxt: 'Failed to download Excel file',
          show: true,
          errorType: 'error',
        }),
      );
      throw error;
    }
  };

  const handleShowDetailsDownload = async (showId?: string) => {
    if (isDownloadingWord) return;

    try {
      setIsDownloadingWord(true);
      const url: any = await handleDownloadShowDetails(undefined, showId);

      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);

          dispatch(
            actions.dispatchToast({
              errorTxt: 'Word document generated successfully',
              show: true,
              errorType: 'success',
            }),
          );
        })
        .catch(error => {
          console.error('Error downloading file:', error);
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Failed to generate Word document',
              show: true,
              errorType: 'error',
            }),
          );
        })
        .finally(() => {
          setIsDownloadingWord(false);
        });

      return url;
    } catch (error) {
      setIsDownloadingWord(false);
      console.error('Error downloading team point report:', error);
      dispatch(
        actions.dispatchToast({
          errorTxt: 'Failed to generate Word document',
          show: true,
          errorType: 'error',
        }),
      );
      throw error;
    }
  };

  const columns = [
    { field: 'teamName', title: 'Team Name' },
    { field: 'rides', title: 'Rides' },
    { field: 'riderCount', title: 'Rider Count' },
    { field: 'scrach', title: 'Scrach' },
  ];

  return (
    <StyledCommonPageWrapper>
      <TitleWrapper>
        <PageMainLabel>Show Details</PageMainLabel>
        <CustomBreadCrumb
          preData={breadcrumbPreData}
          setPreData={setBreadcrumbPreData}
        />
      </TitleWrapper>
      <ButtonWrapper>
        <FilterWrapper>
          <DropdownWrapper>
            <AdminDropDown
              label="Season"
              placeholder="Select Season"
              data={getLookupArray('SEASON', lookUps)}
              onDataChange={handleSeasonChange}
              defaultValue={selectedSeason}
              isLoader={false}
              validations="required"
            />
            <AdminDropDown
              label="Shows"
              placeholder="Select Shows"
              data={transformedShowLookUps}
              onDataChange={handleShowChange}
              defaultValue={selectedShow}
              isLoader={false}
              disabled={selectedSeason.id === ''}
              validations="required"
              popupStyles="showDetails-show-dropdown-popup"
            />
          </DropdownWrapper>
          <ActionWrapper>
            <AdminCustomButton
              buttonType="cancel"
              onClick={handleCancelClick}
              disabled={selectedShow.id === ''}
              className="action-button"
            >
              Cancel
            </AdminCustomButton>
            <AdminCustomButton
              buttonType="save"
              onClick={handleSaveClick}
              disabled={selectedShow.id === ''}
              className="action-button"
            >
              Filter
            </AdminCustomButton>
          </ActionWrapper>
        </FilterWrapper>
        <AdminCustomButton
          buttonType="primary"
          isActive={false}
          className="fill-button download-details-style"
          disabled={selectedShow.id === '' || isDownloadingWord}
          onClick={() => handleShowDetailsDownload(selectedShow.id)}
        >
          Generate Word Document
        </AdminCustomButton>
        <AdminCustomButton
          buttonType="primary"
          isActive={false}
          className="fill-button"
          disabled={
            selectedShow.id === '' ||
            showDetailsData?.teams?.length === 0 ||
            isDownloadingExcel
          }
          onClick={handleDownloadRiderpointReport}
        >
          Download Excel
        </AdminCustomButton>
      </ButtonWrapper>
      {(showDetailsData?.pager?.count ?? 0) > 10 && (
        <EntriesPerPageDropDownWrapper>
          <AdminDropDown
            label="No: of entries per page"
            onDataChange={handlePageSizeChange}
            defaultValue={pageSizeSelected}
            data={pageSizeOptions}
          />
        </EntriesPerPageDropDownWrapper>
      )}
      {loading ? (
        <TableSkeletonLoader rowContent={5} width={100} headingWidth={40} />
      ) : (
        <CustomDataGrid
          data={showDetailsData?.teams ?? []}
          columns={columns as GridColumnProps[]}
          onPageChange={event => {
            handlePageChange(event);
          }}
          pageSize={pageSize}
          totalCount={showDetailsData?.pager?.count}
          skip={pageSkip}
          showPagination
        />
      )}
    </StyledCommonPageWrapper>
  );
};

const StyledCommonPageWrapper = styled(CommonPageWrapper)`
  padding-bottom: 52px;
  @media (min-height: 100px) and (max-height: 560px) {
    padding-bottom: 0px;
  }
  .k-virtual-content {
    max-height: calc(100vh - 285px);
    min-height: unset !important;
    height: 100% !important;

    @media (min-height: 100px) and (max-height: 560px) {
      max-height: calc(100vh - 311px);
    }
  }
  height: 100vh;
  .top-header-style {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    div:nth-child(1) {
      width: 100%;
    }
    .dynamic-title {
      width: 100%;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .date {
      font-size: 13px !important;
      font-weight: 400 !important;
      padding-top: 7px !important;
    }
  }
  .k-grid-header {
    table {
      tr {
        th {
          padding: 10px 4px 9px 4px !important;
          vertical-align: top;
          &:has(.green-bg) {
            background-color: #8ee08e !important;
          }
          &:nth-child(n + 5) {
            position: unset !important;
            border-right: unset !important;
            border-left: unset !important;
            text-align: center;
          }
        }
      }
    }
  }
  .k-grid-container {
    table {
      tr td:nth-child(n + 5) {
        position: unset !important;
        border-right: unset !important;
        border-left: unset !important;
        > div {
          text-align: center;
        }
      }
    }
  }
  .k-grid-content tr {
    .k-grid-content-sticky {
      cursor: unset !important;
      :hover {
        cursor: unset !important;
        background: inherit !important;
      }
    }
    :hover {
      td {
        background: inherit !important;
      }
    }
  }
  .k-grid tbody > tr:not(.k-detail-row):hover,
  .k-grid tbody > tr:not(.k-detail-row).k-hover {
    background-color: #dddddd !important;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0 30px 0;
  .fill-button {
    margin-right: 0px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  .fill-button {
    button {
      margin-top: 5px;
      height: 31px;
    }
    &:has(.k-disabled) {
      cursor: not-allowed;
    }
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0px 0px 20px 0px;
`;

const DropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 40%;
  gap: 15px;
  .SHdropdown {
    width: 50%;
    .k-dropdownlist {
      max-height: 31px;
      border-radius: 8px !important;
    }
  }
  .k-input-inner {
    padding: 0 5px !important;
  }
  .k-input-value-text {
    margin: 2px 4px 2px 4px;
    font-size: 12px !important;
    letter-spacing: 0px;
    color: #1e1e1e;
    text-align: left;
  }
`;

const ActionWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: start;
  width: 40%;
  padding-top: 25px;
  padding-left: 15px;
  .k-button {
    height: 31px !important;
    span {
      margin: 0px 15px;
    }
  }
  .action-button:has(.k-disabled) {
    cursor: not-allowed;
  }
`;

const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: 10px;

  .SHdropdown {
    width: 160px;

    .label {
      margin-bottom: 8px;
      font-size: 13px;
      color: #475467;
      font-weight: 500;
    }

    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }

    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }

    .k-input-value-text {
      margin: 4px 0;
      font-size: 13px !important;
      color: #1e1e1e;
    }
  }
`;